import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faRotate } from '@fortawesome/free-solid-svg-icons';
import ActivityTimeline from '../../components/timelines/ActivityTimeline';
import { useFactoryEvents, useFactoryEventFacets } from '../../features/factory-events/hooks';

/**
 * One page, two uses: Activity (what people did) and Reports (what the machines
 * did). They differ by the `kind` they ask for and by nothing else, so they are
 * the same component rather than two that drift apart.
 *
 * What it adds over the dashboard cards is the thing those cannot have: a day.
 * The cards read the newest hundred events; this reads a date range out of
 * MongoDB, which is why the history had to be written there first.
 */

/** Local calendar day as YYYY-MM-DD — the format the API's `day` param takes. */
const toDayString = date => {
  // en-CA renders ISO-shaped dates, and going through the formatter keeps this
  // in the browser's own timezone. `toISOString()` would convert to UTC and, for
  // anyone east of Greenwich, hand back yesterday for the first hours of the day.
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

const addDays = (day, delta) => {
  const [y, m, d] = day.split('-').map(Number);
  // Constructed at noon: stepping a day across a DST change from midnight can
  // land back on the same date.
  const at = new Date(y, m - 1, d, 12, 0, 0);
  at.setDate(at.getDate() + delta);
  return toDayString(at);
};

const prettyDay = day => {
  const [y, m, d] = day.split('-').map(Number);
  return new Intl.DateTimeFormat('ro-RO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(new Date(y, m - 1, d, 12));
};

const PAGE_SIZE = 100;

const FactoryEventsPage = ({ kind, title, subtitle }) => {
  const today = toDayString(new Date());

  const [day, setDay] = useState(today);
  const [page, setPage] = useState(1);
  const [type, setType] = useState('');
  const [user, setUser] = useState('');
  const [machineId, setMachineId] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  // Only what is actually set is sent. An empty string as a filter value would
  // ask the backend for events whose user is "" and match nothing.
  const params = useMemo(() => {
    const p = { kind, day, page, limit: PAGE_SIZE };
    if (type) p.type = type;
    if (user) p.user = user;
    if (machineId) p.machineId = machineId;
    if (q) p.q = q;
    return p;
  }, [kind, day, page, type, user, machineId, q]);

  const { data, isLoading, isFetching, isError, error, refetch } = useFactoryEvents(params);
  // Facets follow the day, not the filters — otherwise choosing a person empties
  // the list of people and there is no way back to the others.
  const { data: facets } = useFactoryEventFacets({ kind, day });

  const goToDay = next => {
    setDay(next);
    setPage(1);
  };

  const resetFilters = () => {
    setType('');
    setUser('');
    setMachineId('');
    setSearch('');
    setQ('');
    setPage(1);
  };

  const filtersActive = Boolean(type || user || machineId || q);
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;

  const apiError =
    error?.response?.data?.error || error?.message || 'Could not load the event history';

  return (
    <div className="pb-9">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-2">
        <div>
          <h2 className="mb-1">{title}</h2>
          <p className="text-700 mb-0">{subtitle}</p>
        </div>
        <Button variant="phoenix-secondary" size="sm" onClick={() => refetch()}>
          <FontAwesomeIcon icon={faRotate} spin={isFetching} className="me-1" />
          Refresh
        </Button>
      </div>

      {/* ── the day ────────────────────────────────────────────────────── */}
      <Card className="mb-3">
        <Card.Body className="py-3">
          <div className="d-flex flex-wrap align-items-center justify-content-center gap-3">
            <Button
              variant="phoenix-secondary"
              size="sm"
              aria-label="Previous day"
              onClick={() => goToDay(addDays(day, -1))}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>

            <div className="text-center" style={{ minWidth: 260 }}>
              <div className="fw-bold text-1000">{prettyDay(day)}</div>
              <div className="text-700 fs-9">
                {total.toLocaleString()} event{total === 1 ? '' : 's'}
                {filtersActive ? ' matching the filters' : ''}
              </div>
            </div>

            <Button
              variant="phoenix-secondary"
              size="sm"
              aria-label="Next day"
              // Nothing has happened tomorrow yet
              disabled={day >= today}
              onClick={() => goToDay(addDays(day, 1))}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>

            <Form.Control
              type="date"
              size="sm"
              value={day}
              max={today}
              onChange={e => e.target.value && goToDay(e.target.value)}
              style={{ width: 160 }}
            />

            {day !== today && (
              <Button variant="link" size="sm" className="p-0" onClick={() => goToDay(today)}>
                Today
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* ── the filters ────────────────────────────────────────────────── */}
      <Card className="mb-3">
        <Card.Body className="py-3">
          <Row className="g-2 align-items-end">
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fs-9 text-700 mb-1">Type</Form.Label>
              <Form.Select
                size="sm"
                value={type}
                onChange={e => {
                  setType(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All types</option>
                {(facets?.types ?? []).map(t => (
                  <option key={t} value={t}>
                    {t.replaceAll('_', ' ')}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fs-9 text-700 mb-1">Who</Form.Label>
              <Form.Select
                size="sm"
                value={user}
                onChange={e => {
                  setUser(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Everyone</option>
                {(facets?.users ?? []).map(u => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg={2}>
              <Form.Label className="fs-9 text-700 mb-1">Machine</Form.Label>
              <Form.Select
                size="sm"
                value={machineId}
                onChange={e => {
                  setMachineId(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All machines</option>
                {(facets?.machines ?? []).map(m => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fs-9 text-700 mb-1">Search</Form.Label>
              <Form.Control
                size="sm"
                placeholder="product, code, text…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                // Applied on Enter rather than per keystroke: each change is a
                // request, and a regex query over the history is not free.
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    setQ(search.trim());
                    setPage(1);
                  }
                }}
                onBlur={() => {
                  if (search.trim() !== q) {
                    setQ(search.trim());
                    setPage(1);
                  }
                }}
              />
            </Col>

            <Col xs={12} lg={1}>
              <Button
                variant="phoenix-secondary"
                size="sm"
                className="w-100"
                disabled={!filtersActive}
                onClick={resetFilters}
              >
                Clear
              </Button>
            </Col>
          </Row>

          {filtersActive && (
            <div className="d-flex flex-wrap gap-2 mt-3">
              {type && <Badge bg="primary" variant="phoenix">{type.replaceAll('_', ' ')}</Badge>}
              {user && <Badge bg="info" variant="phoenix">{user}</Badge>}
              {machineId && <Badge bg="warning" variant="phoenix">{machineId}</Badge>}
              {q && <Badge bg="secondary" variant="phoenix">“{q}”</Badge>}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ── the events ─────────────────────────────────────────────────── */}
      <Card>
        <Card.Body>
          {isError ? (
            <Alert variant="danger" className="mb-0">
              {apiError}
            </Alert>
          ) : isLoading ? (
            <div className="d-flex justify-content-center py-6">
              <Spinner animation="border" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-6 text-700">
              {filtersActive
                ? 'Nothing matches these filters on this day.'
                : 'Nothing was recorded on this day.'}
            </div>
          ) : (
            <>
              <div className="scrollbar" style={{ overflowX: 'hidden' }}>
                <ActivityTimeline reports={rows} />
              </div>

              {pages > 1 && (
                <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
                  <Button
                    variant="phoenix-secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                    Newer
                  </Button>
                  <span className="text-700 fs-9">
                    Page {page} of {pages}
                  </span>
                  <Button
                    variant="phoenix-secondary"
                    size="sm"
                    disabled={page >= pages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Older
                    <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default FactoryEventsPage;
